import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Tappable, Textarea } from '@vectord/components';
import { batch, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import cogoToast from 'cogo-toast';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  CAPSTONE_SUBMISSION_STATUS,
  resolveSubmissionModalStateSelector,
  setSelectedSubmission,
  setResolveSubmissionModalState,
  useUpdateCapstoneSubmissionStatusMutation,
  selectedSubmissionSelector,
} from 'features/neovarsityManagement';
import Field from 'components/Field';
import SectionHeading from 'components/SectionHeading';
import styles from './ResolveSubmissionModal.module.scss';
import GlobalLoader from 'components/GlobalLoader';

const ResolveSubmissionModal = () => {
  const [updateStatus, { isLoading, error, isSuccess }] =
    useUpdateCapstoneSubmissionStatusMutation();
  const dispatch = useDispatch();
  const isModalOpen = useSelector(resolveSubmissionModalStateSelector);
  const selectedSubmission = useSelector(selectedSubmissionSelector);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Status of capstone submission updated successfully.');
      dispatch(setResolveSubmissionModalState(false));
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      cogoToast.error(error.data?.message || DEFAULT_ERROR_MESSAGE);
    }
  }, [dispatch, error]);

  const handleCloseModal = useCallback(() => {
    batch(() => {
      dispatch(setSelectedSubmission(null));
      dispatch(setResolveSubmissionModalState(false));
    });
  }, [dispatch]);

  const handleApproveClick = useCallback(() => {
    updateStatus({
      status: CAPSTONE_SUBMISSION_STATUS.approved,
      reason,
      submissionId: selectedSubmission.id,
    });
  }, [reason, selectedSubmission, updateStatus]);

  const handleRejectClick = useCallback(() => {
    if (reason.trim().length === 0) {
      cogoToast.error('Please enter a reason for rejecting submission');
      return;
    }
    updateStatus({
      status: CAPSTONE_SUBMISSION_STATUS.rejected,
      reason,
      submissionId: selectedSubmission.id,
    });
  }, [reason, selectedSubmission, updateStatus]);

  return (
    <Modal
      withoutHeader
      unMountOnClose
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      size="large"
      className="p-m"
    >
      <GlobalLoader isVisible={isLoading} />
      <SectionHeading title="Resolve this submission" />
      <Field
        label="Enter Feedback With Learner"
        hint="Feedback is only required if you are rejecting a submission"
      >
        <Textarea
          className={classNames(styles.note, 'scroll')}
          value={reason}
          maxRows={8}
          minRows={4}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div className="row justify-end">
        <Tappable
          className="btn btn-primary btn-long m-r-5"
          onClick={handleApproveClick}
          disabled={isLoading}
        >
          Approve
        </Tappable>
        <Tappable
          className={classNames('btn btn-long', {
            'btn-danger': reason.trim().length >= 0,
          })}
          onClick={handleRejectClick}
          disabled={reason.trim().length === 0 || isLoading}
        >
          Reject
        </Tappable>
      </div>
    </Modal>
  );
};
export default ResolveSubmissionModal;
